import { defineStore, storeToRefs } from 'pinia'
import { ref, watch, computed } from 'vue'
import useEditionStageStore from '@/stores/EditionStage'
import { TIDIO_STYLES } from '@/constants/tidio'

import type { RouteLocation } from 'vue-router'

export const useTidio = defineStore('Tidio', () => {
  const editionStageStore = useEditionStageStore()

  const isDisplayChat = ref<boolean>(true)
  const isHideChatOnClose = ref<boolean>(false)
  const currentTidioStyles = ref<string>('')
  const tidioStylesHash = ref<string>('')

  const disableTidioChat = () => {
    isDisplayChat.value = false
    isHideChatOnClose.value = true
    if (window.tidioChatApi) window.tidioChatApi.display(false)
  }

  const isTidioChatEnabledBasedOnEditionStage = computed<boolean>(
    () => editionStageStore.isBeforeCampaignSecure || editionStageStore.isCampaignUnderway
  )

  const tidioEditionStageGuard = () => {
    if (!isTidioChatEnabledBasedOnEditionStage.value) disableTidioChat()
    return isTidioChatEnabledBasedOnEditionStage.value
  }

  const { editionStage } = storeToRefs(editionStageStore)
  watch(
    editionStage,
    () => {
      tidioEditionStageGuard()
    },
    { immediate: true }
  )

  const checkIfTidioChatApiExists = (): boolean => {
    if (!window.tidioChatApi) {
      console.error('Tidio chat api is not loaded')
      return false
    }
    return true
  }

  const setChatVisibilityBasedOnRefs = (): void => {
    if (!checkIfTidioChatApiExists()) return
    if (!tidioEditionStageGuard()) return
    if (isDisplayChat.value) {
      window.tidioChatApi.display(true)
    } else {
      window.tidioChatApi.hide()
    }
  }

  const onChatClose = (): void => {
    if (!checkIfTidioChatApiExists()) return
    window.tidioChatApi.on('close', () => {
      if (isHideChatOnClose.value) {
        isDisplayChat.value = false
        window.tidioChatApi.hide()
      } else {
        window.tidioChatApi.close()
      }
    })
  }

  const onChatReady = (): void => {
    setChatVisibilityBasedOnRefs()
    onChatClose()
    document.removeEventListener('tidioChat-ready', onChatReady)
  }

  const setDisplayAndHideOnCloseFromMeta = (to: RouteLocation): void => {
    if (typeof to.meta.initialIsDisplayChat === 'boolean')
      isDisplayChat.value = to.meta.initialIsDisplayChat
    if (typeof to.meta.initialIsHideChatOnClose === 'boolean')
      isHideChatOnClose.value = to.meta.initialIsHideChatOnClose
  }

  const setPagePositionStyles = (to: RouteLocation): void => {
    currentTidioStyles.value =
      typeof to.meta.tidioCustomPositionStyles === 'string'
        ? to.meta.tidioCustomPositionStyles
        : TIDIO_STYLES.DEFAULT
  }

  const applyPositionStylesBasedOnRefs = (): void => {
    if (!checkIfTidioChatApiExists()) return
    if (tidioStylesHash.value) window.tidioChatApi.removeStyles(tidioStylesHash.value)
    tidioStylesHash.value = window.tidioChatApi.adjustStyles(currentTidioStyles.value)
  }

  const initChatOnPage = (to: RouteLocation): void => {
    tidioEditionStageGuard()
    setDisplayAndHideOnCloseFromMeta(to)
    setPagePositionStyles(to)
    applyPositionStylesBasedOnRefs()
    if (window.tidioChatApi) {
      window.tidioChatApi.on('ready', onChatReady)
    } else {
      document.addEventListener('tidioChat-ready', onChatReady)
    }
  }

  const openChat = () => {
    if (!checkIfTidioChatApiExists()) return
    tidioEditionStageGuard()
    isDisplayChat.value = true
    window.tidioChatApi.display(isDisplayChat.value)
    window.tidioChatApi.open()
  }

  return {
    currentTidioStyles,
    isDisplayChat,
    isHideChatOnClose,
    initChatOnPage,
    openChat,
    setChatVisibilityBasedOnRefs,
    isTidioChatEnabledBasedOnEditionStage
  }
})
